import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '@citiguide/frontend-core';
import { CategoryChip } from '@citiguide/ui-components';
import { CHAT_KINDS, CHAT_PRICE_BANDS, CHAT_RADII_M } from '@citiguide/shared';
import type { ChatStatusResponse } from '@citiguide/shared';
import { hasActiveFilters, useCurationStore } from '../../store/curationStore';
import { useMenuStore } from '../../store/menuStore';
import { useCategoriesWithGroups } from '../../hooks/useCategories';

interface StateChipsProps {
  /** Which filter fields the server supports (status.fields); price/kind/near only show when listed. */
  fields: ChatStatusResponse['fields'];
}

/**
 * The filter state as editable chips. Editing a chip changes the curation store only (and so the URL
 * and the marker query); it never calls the model and never touches the transcript.
 */
export default function StateChips({ fields }: StateChipsProps) {
  const { t } = useTranslation();
  const state = useCurationStore((s) => s.state);
  const setCategories = useCurationStore((s) => s.setCategories);
  const setAudience = useCurationStore((s) => s.setAudience);
  const setPrice = useCurationStore((s) => s.setPrice);
  const setKind = useCurationStore((s) => s.setKind);
  const setNear = useCurationStore((s) => s.setNear);
  const clearFilters = useCurationStore((s) => s.clearFilters);
  const toggleMainMenu = useMenuStore((s) => s.toggleMainMenu);
  const hasLocation = useUserStore((s) => s.location !== null);
  const { data: tree } = useCategoriesWithGroups();

  const labelOf = useMemo(() => {
    const names = new Map<string, string>();
    for (const g of tree?.group ?? []) if (g.name) names.set(g.id, g.name);
    for (const c of tree?.category ?? []) if (c.name) names.set(c.id, c.name);
    return (id: string) => names.get(id) ?? id;
  }, [tree]);

  const removable = (label: string, onRemove: () => void, key: string) => (
    <CategoryChip
      key={key}
      label={`${label} ×`}
      active
      aria-pressed={undefined}
      aria-label={t('chat.removeChip', { label })}
      onClick={onRemove}
    />
  );

  const showNear = fields.includes('near');

  return (
    <div className="chat-chips" role="group" aria-label={t('chat.filters')}>
      {state.categories.map((id) =>
        removable(labelOf(id), () => setCategories(state.categories.filter((c) => c !== id)), `cat-${id}`),
      )}
      {state.audience.map((id) =>
        removable(labelOf(id), () => setAudience(state.audience.filter((a) => a !== id)), `aud-${id}`),
      )}
      {fields.includes('price') &&
        CHAT_PRICE_BANDS.map((band) => (
          <CategoryChip
            key={`price-${band}`}
            label={t(`chat.price.${band}`)}
            active={state.price === band}
            onClick={() => setPrice(state.price === band ? null : band)}
          />
        ))}
      {fields.includes('kind') &&
        CHAT_KINDS.map((kind) => (
          <CategoryChip
            key={`kind-${kind}`}
            label={t(`chat.kind.${kind}`)}
            active={state.kind === kind}
            onClick={() => setKind(state.kind === kind ? null : kind)}
          />
        ))}
      {showNear &&
        CHAT_RADII_M.map((radius) => (
          <CategoryChip
            key={`near-${radius}`}
            label={`${t('chat.near.label')} ${t('chat.near.radius', { radius })}`}
            active={state.near?.radius_m === radius}
            disabled={!hasLocation && state.near?.radius_m !== radius}
            onClick={() => setNear(state.near?.radius_m === radius ? null : { radius_m: radius })}
          />
        ))}
      <CategoryChip label={t('chat.addFilter')} onClick={toggleMainMenu} />
      {hasActiveFilters(state) && <CategoryChip label={t('chat.clearFilters')} onClick={clearFilters} />}
      {showNear && !hasLocation && <span className="chat-hint">{t('chat.near.needsLocation')}</span>}
    </div>
  );
}
